import { computed, readonly, ref, watch } from "vue";
import {
  RouteNames,
  feedRouteContext,
  packageRouteContext,
} from "@/router-helpers";

type AccountType =
  | "internal"
  | "customer"
  | "reseller"
  | "external"
  | "distributor"
  | "billboard"
  | "referrer"
  | "demo";

type ExternalAccount = {
  id: number;
  providerSlug: string;
  showScreenfeed: boolean;
  showDeliveryMethods: boolean;
  showAccountName: boolean;
  showSelectFeed: boolean;
  showNavigationLinks: boolean;
  showSaveFeedButton: boolean;
  showFeedName: boolean;
  expandFeedOptions: boolean;
  allowedOriginUrls?: string;
  usingIFrame: boolean;
  usingPostMessage: boolean;
  themeName?: string;
};

type UserProfile = {
  role: string[];
  /** @deprecated Use account.ts accountContext if possible as it is reseller child account aware*/
  accountId?: number;
  childAccountId?: number;
  email: string;
  email_verified: boolean;
  unique_name: string;
  /** @deprecated Use account.ts accountContext if possible as it is reseller child account aware*/
  accountType: AccountType | undefined;
  externalAccount: ExternalAccount;
  externalFrameOrigin?: string;
};

function tryParseNumber(value: unknown) {
  if (typeof value === "string") {
    const number = parseInt(value, 10);
    if (!isNaN(number)) {
      return number;
    }
  }
  return undefined;
}

function getClaim<T>(
  profile: Record<string, any>,
  claimName: string,
): T | undefined {
  return (
    (profile[`https://schema.screenfeed.com/claims/${claimName}`] as T) ||
    undefined
  );
}

export function hasExternalAccount(
  userProfile: Record<string, any> | null,
): boolean {
  if (!userProfile) return false;

  const ea = getClaim<ExternalAccount>(userProfile, "externalAccount");
  if (!ea) return false;

  return true;
}

function getDisplayName(oidcProfile: Record<string, any>): string {
  if (oidcProfile.unique_name?.trim()) return oidcProfile.unique_name.trim();

  if (oidcProfile.preferred_username?.trim())
    return oidcProfile.preferred_username.trim();

  if (oidcProfile.name?.trim()) return oidcProfile.name.trim();

  if (oidcProfile.email?.trim()) {
    const emailLocal = oidcProfile.email.split("@")[0];
    return emailLocal.trim();
  }
  return "User";
}

export type UserState = ReturnType<typeof createUserState>;

export function createUserState() {
  const profile = ref<UserProfile | null>(null);
  const demoFrameOriginUrl = "https://demo.integratordomain.com";
  const _targetOrigin = ref<string | undefined>(undefined);
  const isProfileLoading = ref<boolean>(false);

  watch(isProfileLoading, (val) => {
    if (val) {
      setTimeout(() => {
        isProfileLoading.value = false;
      }, 1000);
    }
  });

  function initProfile() {
    isProfileLoading.value = true;
    profile.value = null;
  }

  function updateUserProfile(payload: unknown) {
    if (payload === undefined) {
      profile.value = null;
      isProfileLoading.value = false;
      return;
    }

    const oidcProfile = payload as Record<string, any>;
    const ea = getClaim<string>(oidcProfile, "externalAccount");

    const result = {
      role: oidcProfile.role,
      accountId: tryParseNumber(getClaim<number>(oidcProfile, "accountId")),
      childAccountId: tryParseNumber(
        getClaim<number>(oidcProfile, "childAccountId"),
      ),
      email: oidcProfile.email,
      email_verified: oidcProfile.email_verified === "true",
      unique_name: getDisplayName(oidcProfile),
      accountType: getClaim<AccountType>(oidcProfile, "accountType"),
      externalAccount: ea ? JSON.parse(ea) : null,
      externalFrameOrigin: getClaim<string>(oidcProfile, "frameOrigin"),
    };

    profile.value = result;
    isProfileLoading.value = false;
  }

  const isExternalAccount = computed(
    () => profile.value?.externalAccount?.id !== undefined,
  );

  const frameOriginUrl = computed<string | undefined>(() => {
    return profile.value?.externalFrameOrigin;
  });

  const isUsingDemoOrigin = computed<boolean>(() => {
    return frameOriginUrl.value === demoFrameOriginUrl;
  });

  const allowedOriginUrls = computed<string[] | undefined>(() => {
    if (frameOriginUrl.value === demoFrameOriginUrl) {
      console.warn("Using demo origin url");
      return ["*"];
    }

    if (frameOriginUrl.value) return [frameOriginUrl.value];

    const urls = profile.value?.externalAccount?.allowedOriginUrls;
    if (!urls) return undefined;
    return urls.split(",").map((url) => url.trim());
  });

  const targetOrigin = computed({
    get() {
      if (frameOriginUrl.value === demoFrameOriginUrl) {
        console.warn("Using demo origin url");
        return "*";
      }

      if (frameOriginUrl.value) return frameOriginUrl.value;

      return _targetOrigin.value;
    },
    set(value: string | undefined) {
      if (!value) return;
      try {
        new URL(value);
        _targetOrigin.value = value;
        console.info("Setting targetOrigin to:", value);
      } catch {
        console.warn("Invalid URL for targetOrigin:", value);
      }
    },
  });

  const shouldTryRegisterPartner = computed<boolean>(() => {
    return (
      isExternalAccount.value === true &&
      profile.value?.externalAccount.usingIFrame === true &&
      profile.value?.externalAccount.usingPostMessage === true
    );
  });

  const isUsingPostMessage = computed<boolean>(() => {
    return (
      isExternalAccount.value === true &&
      profile.value?.externalAccount.usingPostMessage === true
    );
  });

  function iframeAllowedRoutes() {
    if (!isExternalAccount.value) return undefined;

    const isFramed = window.self !== window.top;
    if (isFramed && profile.value?.externalAccount.providerSlug === "yodeck")
      return [
        feedRouteContext(RouteNames.FeedConfig),
        packageRouteContext(RouteNames.FeedConfig),
      ];

    return undefined;
  }

  return {
    user: readonly(profile),
    isUserLoading: readonly(isProfileLoading),
    initProfile,
    updateUserProfile,
    isExternalUser: isExternalAccount,
    frameOriginUrl,
    isUsingDemoOrigin,
    allowedOriginUrls,
    targetOrigin,
    shouldTryRegisterPartner,
    isUsingPostMessage,
    iframeAllowedRoutes,
  };
}

const defaultUserState = createUserState();

/** @deprecated Import and use useUserState instead.*/
export const {
  user,
  updateUserProfile,
  isExternalUser,
  frameOriginUrl,
  isUsingDemoOrigin,
  allowedOriginUrls,
  targetOrigin,
  shouldTryRegisterPartner,
  isUsingPostMessage,
  iframeAllowedRoutes,
} = defaultUserState;

export function useUserState(): UserState {
  return defaultUserState;
}
